import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class ProductDescriptionToggle extends HTMLElement {
  #abortController = new AbortController();
  #resizeObserver;
  #animationFrame;

  connectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    const button = this.querySelector('[data-description-toggle]');
    const text = this.querySelector('[data-description-text]');
    if (!(button instanceof HTMLButtonElement) || !(text instanceof HTMLElement)) return;

    button.addEventListener('click', this.#handleToggle, { signal: this.#abortController.signal });
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#updateOverflow);
    this.#resizeObserver.observe(this);
    this.#animationFrame = requestAnimationFrame(this.#updateOverflow);
    document.fonts?.ready.then(this.#updateOverflow).catch(() => {});
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#animationFrame);
  }

  #handleToggle = () => {
    const button = this.querySelector('[data-description-toggle]');
    const text = this.querySelector('[data-description-text]');
    if (!(button instanceof HTMLButtonElement) || !(text instanceof HTMLElement)) return;

    const expanded = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = expanded ? button.dataset.lessLabel || 'Ver menos' : button.dataset.moreLabel || 'Ver más';
    text.classList.toggle('is-expanded', expanded);
  };

  #updateOverflow = () => {
    const button = this.querySelector('[data-description-toggle]');
    const text = this.querySelector('[data-description-text]');
    if (!(button instanceof HTMLButtonElement) || !(text instanceof HTMLElement)) return;

    const wasExpanded = button.getAttribute('aria-expanded') === 'true';
    text.classList.remove('is-expanded');
    const overflows = text.scrollHeight > text.clientHeight + 1;
    text.classList.toggle('is-expanded', wasExpanded && overflows);
    button.hidden = !overflows;
    if (!overflows) {
      button.setAttribute('aria-expanded', 'false');
      button.textContent = button.dataset.moreLabel || 'Ver más';
    }
  };
}

if (!customElements.get('product-description-toggle')) {
  customElements.define('product-description-toggle', ProductDescriptionToggle);
}

class ProductComplementsRandom extends HTMLElement {
  connectedCallback() {
    const items = Array.from(this.querySelectorAll('[data-random-complement-item]'));
    const count = Math.max(1, Number(this.dataset.count) || 1);

    for (let index = items.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(this.#random() * (index + 1));
      [items[index], items[randomIndex]] = [items[randomIndex], items[index]];
    }

    for (const [index, item] of items.entries()) {
      this.append(item);
      item.hidden = index >= count;
      const input = item.querySelector('input[data-premium-complement]');
      if (input instanceof HTMLInputElement && item.hidden) input.checked = false;
    }
  }

  #random() {
    if (!globalThis.crypto?.getRandomValues) return Math.random();
    const value = new Uint32Array(1);
    globalThis.crypto.getRandomValues(value);
    return value[0] / 2 ** 32;
  }
}

if (!customElements.get('product-complements-random')) {
  customElements.define('product-complements-random', ProductComplementsRandom);
}

const SUBSCRIPTION_MAX_QUANTITY = 2;

class ProductVolumeTiers extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    this.addEventListener('change', this.#handleChange, { signal });
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange, { signal });

    const quantity = this.#getQuantity();
    this.#selectTier(quantity);
    this.#update(quantity);
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** Re-broadcasts the current total, used by elements that upgrade after this one. */
  refresh() {
    this.#update(this.#getQuantity());
  }

  get #mode() {
    return this.dataset.mode === 'subscription' ? 'subscription' : 'one_time';
  }

  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    if (input.hasAttribute('data-mode-input')) {
      this.dataset.mode = input.value;
      const quantity = this.#getQuantity();
      if (this.#mode === 'subscription' && quantity > SUBSCRIPTION_MAX_QUANTITY) {
        this.#setQuantity(SUBSCRIPTION_MAX_QUANTITY);
      } else {
        this.#update(quantity);
      }
      return;
    }

    const quantity = Number(input.value);
    if (!Number.isInteger(quantity) || quantity < 1) return;
    this.#selectTier(quantity);
    this.#setQuantity(quantity);
  };

  #handleQuantityChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('product-form-component')) return;
    const quantity = Number(event.detail?.quantity) || 1;

    if (this.#mode === 'subscription' && quantity > SUBSCRIPTION_MAX_QUANTITY) {
      this.#setQuantity(SUBSCRIPTION_MAX_QUANTITY);
      return;
    }

    this.#selectTier(quantity);
    this.#update(quantity);
  };

  #getQuantitySelector() {
    return this.closest('.shopify-section')?.querySelector('product-form-component quantity-selector-component');
  }

  #getQuantity() {
    const input = this.closest('.shopify-section')?.querySelector('product-form-component input[name="quantity"]');
    return Number(input?.value) || 1;
  }

  /** Updates the form quantity; the resulting quantity update event refreshes the tiers. */
  #setQuantity(quantity) {
    const quantitySelector = this.#getQuantitySelector();
    if (!quantitySelector) {
      this.#selectTier(quantity);
      this.#update(quantity);
      return;
    }

    quantitySelector.setValue(String(quantity));
    quantitySelector.onQuantityChange();
    quantitySelector.updateButtonStates();
  }

  #selectTier(quantity) {
    for (const input of this.querySelectorAll('input[data-tier-input]')) {
      const selected = Number(input.value) === quantity;
      input.checked = selected;
      input.closest('.premium-volume-tier')?.classList.toggle('is-selected', selected);
    }
  }

  /** Syncs the selling plan input and the purchase total with the current mode and quantity. */
  #update(quantity) {
    const subscribed = this.#mode === 'subscription';
    const usesSecondPlan = quantity >= 2;
    const sellingPlanInput = this.querySelector('input[data-selling-plan-input]');

    if (sellingPlanInput instanceof HTMLInputElement) {
      sellingPlanInput.disabled = !subscribed;
      sellingPlanInput.value = (usesSecondPlan ? this.dataset.secondPlanId : this.dataset.firstPlanId) || '';
    }

    const unitPrice = subscribed
      ? Number(usesSecondPlan ? this.dataset.secondPlanPrice : this.dataset.firstPlanPrice)
      : Number(this.dataset.unitPrice);

    this.dispatchEvent(
      new CustomEvent('premium-purchase:total', {
        bubbles: true,
        detail: { total: (unitPrice || 0) * quantity, currency: this.dataset.currency },
      })
    );
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html || (detail.productId && detail.productId !== this.dataset.productId)) return;
        const selector = `[data-volume-tiers-id="${CSS.escape(this.dataset.volumeTiersId || '')}"]`;
        const replacement = detail.html.querySelector(selector);
        if (!(replacement instanceof HTMLElement)) return;

        const clone = /** @type {HTMLElement} */ (replacement.cloneNode(true));
        const modeInput = clone.querySelector(`input[data-mode-input][value="${this.#mode}"]`);
        if (modeInput instanceof HTMLInputElement) {
          modeInput.checked = true;
          clone.dataset.mode = this.#mode;
        }
        this.replaceWith(clone);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-volume-tiers] Variant update failed:', error);
      });
  };
}

if (!customElements.get('product-volume-tiers')) {
  customElements.define('product-volume-tiers', ProductVolumeTiers);
}

class ProductPurchaseTotal extends HTMLElement {
  #abortController = new AbortController();
  #unitPrice = 0;

  connectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#unitPrice = Number(this.dataset.unitPrice) || 0;
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange, { signal });
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    section?.addEventListener('premium-purchase:total', this.#handlePremiumTotal, { signal });
    section?.querySelector('product-volume-tiers')?.refresh?.();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** When the volume tiers block is present it owns the total, since subscription prices differ per quantity. */
  get #managedByTiers() {
    return Boolean(this.closest('.shopify-section')?.querySelector('product-volume-tiers'));
  }

  #handlePremiumTotal = (event) => {
    const total = Number(event.detail?.total);
    if (!Number.isFinite(total)) return;
    this.#format(total, event.detail?.currency);
  };

  #handleQuantityChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('product-form-component')) return;
    if (this.#managedByTiers) return;
    this.#render(Number(event.detail?.quantity) || 1);
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html || (detail.productId && detail.productId !== this.dataset.productId)) return;
        const selector = `product-purchase-total[data-total-id="${CSS.escape(this.dataset.totalId || '')}"]`;
        const replacement = detail.html.querySelector(selector);
        if (!replacement) return;

        this.#unitPrice = Number(replacement.dataset.unitPrice) || 0;
        this.dataset.unitPrice = replacement.dataset.unitPrice || '0';
        this.dataset.currency = replacement.dataset.currency || this.dataset.currency;
        if (this.#managedByTiers) return;
        const quantityInput = this.closest('product-form-component')?.querySelector('input[name="quantity"]');
        this.#render(Number(quantityInput?.value) || 1);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-purchase-total] Variant update failed:', error);
      });
  };

  #render(quantity) {
    this.#format(this.#unitPrice * quantity);
  }

  #format(cents, currency = this.dataset.currency) {
    if (this.dataset.moneyFormat) {
      this.textContent = formatMoney(cents, this.dataset.moneyFormat, currency || 'USD');
      return;
    }

    this.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(cents / 100);
  }
}

if (!customElements.get('product-purchase-total')) {
  customElements.define('product-purchase-total', ProductPurchaseTotal);
}
